import React, { useContext, useEffect, useRef } from "react";
import themeContext from "../../../../context/theme/ThemeContext";
import "../../styles/styles.css";

/**
 * Per-slice PR list popup — appears when the user clicks the pencil (✏) in the
 * ViewportNavWidget.  Styled to match the History / Clinical Notes AttachmentBox
 * dropdown (history-data-options / dropdown-menu-history classes from styles.css).
 *
 * Props:
 *   items       – Array<{ url, description, creatorName, date, selected }>
 *   onToggle    – (url: string) => void
 *   onToggleAll – (selectAll: boolean) => void
 *   onClose     – () => void
 *   style       – optional override for the container (used for positioning)
 */
const PRListPanel = ({ items = [], onToggle, onToggleAll, onClose, style: overrideStyle }) => {
  const panelRef    = useRef(null);
  const allCheckRef = useRef(null);

  // Theme-aware text colours so the panel is readable on the light/white theme,
  // not just the dark themes (previously hardcoded light greys → low contrast).
  const { getThemeColor } = useContext(themeContext);
  const primaryText = getThemeColor("primary-color");        // #FFF dark / #000 light
  const mutedText   = getThemeColor("downloads-sub-heading"); // #bfbfbf dark / #4d4d4d light

  const allSelected     = items.length > 0 && items.every((i) => i.selected);
  const someSelected    = items.some((i) => i.selected);
  const isIndeterminate = someSelected && !allSelected;

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  // Sync indeterminate checkbox state (can only be set via DOM ref)
  useEffect(() => {
    if (allCheckRef.current) {
      allCheckRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <div
      ref={panelRef}
      className="history-data-options"
      style={{ minWidth: 220, maxHeight: 320, overflowY: "auto", ...overrideStyle }}
    >
      {/* Section heading */}
      <p
        className="heading-history-data-options"
        style={{ borderBottom: "1px solid #3a3a3a", paddingBottom: 7, color: primaryText }}
      >
        Presentation States
      </p>

      {/* All / None row */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px 6px", cursor: "pointer" }}
        onClick={() => onToggleAll?.(!allSelected)}
      >
        <input
          ref={allCheckRef}
          type="checkbox"
          checked={allSelected}
          onChange={() => onToggleAll?.(!allSelected)}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "#0085FF", cursor: "pointer" }}
        />
        <span style={{ fontSize: 13, color: primaryText, userSelect: "none" }}>
          {allSelected ? "Deselect All" : "Select All"}
        </span>
      </div>

      {/* PR list */}
      {items.length === 0 ? (
        <ul className="dropdown-menu-history">
          <li className="no-result" style={{ paddingLeft: 10 }}>
            <a>No PR files for this slice</a>
          </li>
        </ul>
      ) : (
        <ul className="dropdown-menu-history">
          {items.map((item, idx) => (
            <li
              key={item.url}
              onClick={() => onToggle?.(item.url)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px" }}
            >
              <input
                type="checkbox"
                checked={item.selected}
                onChange={() => onToggle?.(item.url)}
                onClick={(e) => e.stopPropagation()}
                style={{ accentColor: "#0085FF", cursor: "pointer", flexShrink: 0 }}
              />
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span
                  title={item.description || `PR ${idx + 1}`}
                  style={{
                    fontSize: 13,
                    color: item.selected ? primaryText : mutedText,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.description || `PR ${idx + 1}`}
                </span>
                {(item.creatorName || item.date) && (
                  <span style={{ fontSize: 11, color: mutedText, marginTop: 1 }}>
                    {[item.creatorName, formatDate(item.date)].filter(Boolean).join(" · ")}
                  </span>
                )}
              </div>
              {/* Active dot */}
              <div
                style={{
                  width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                  backgroundColor: item.selected ? "#0085FF" : "#585858",
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr.length < 8) return "";
  return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
};

export default PRListPanel;
